import React from 'react'
import { faFile } from '@fortawesome/free-solid-svg-icons/faFile'
import { faFilePdf } from '@fortawesome/free-solid-svg-icons/faFilePdf'
import { faFileImage } from '@fortawesome/free-solid-svg-icons/faFileImage'
import { ISortableCallbacks, Sortable } from '../Behavior'
import { FilePreview } from './FileDropProps'
import { ItemTypes } from './ItemTypes'
import { SendableProps, Sendable } from './Sendable'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as Content from '../Content'

const ATTACHMENT_STYLES =
  'flex flex-grow m-1 relative bg-gray-300 bg-opacity-25'
const PREVIEW_WRAPPER_STYLES = 'flex box-border m-0 w-full p-2'
const ICON_STYLES = 'box-border m-0'
const TEXT_CONTENT_STYLES = 'box-border m-0 w-full pl-4 my-auto'

export interface AttachmentPreviewProps {
  /**
   * Indicates whether or not the preview is droppable.
   */
  canDrop?: boolean
  /**
   * A unique id identifying this preview.
   */
  id: string
  /**
   * A file upload preview created by dropzone.
   */
  preview: FilePreview
  /**
   * The current index of this preview in relation to its siblings.
   */
  index: number
  /**
   * The current progress for a pending file upload.
   */
  progress?: number
}

const Icon: React.FC<{ fileId: string }> = ({ fileId }) => {
  const extension = fileId.split('.').reverse()[0]
  let icon = faFile
  switch (extension) {
    case 'pdf':
      icon = faFilePdf
      break
    case 'xls':
    case 'xlsx':
    case 'ppt':
    case 'pptx':
    case 'doc':
    case 'docx':
    case 'csv':
      // Use generic file icon for office documents since specific icons don't exist in free version
      icon = faFile
      break
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'svg':
      icon = faFileImage
      break
  }
  return <FontAwesomeIcon icon={icon} size="2x" />
}

export const AttachmentPreview: React.FC<
  AttachmentPreviewProps & SendableProps & ISortableCallbacks
> = ({
  id,
  canDrop,
  preview,
  progress,
  onSort,
  onFinishSort,
  onDelete,
  index,
}) => (
  <Sortable
    id={id}
    index={index}
    type={ItemTypes.File}
    onSort={onSort}
    onFinishSort={onFinishSort}
    sortDirection="vertical"
  >
    <div className={ATTACHMENT_STYLES}>
      <Sendable
        onDelete={onDelete}
        deletable={canDrop}
        progress={progress}
        preview={preview}
        listMode={true}
      >
        {preview ? (
          <div className={PREVIEW_WRAPPER_STYLES}>
            <div className={ICON_STYLES}>
              <Icon fileId={preview.id} />
            </div>
            <div className={TEXT_CONTENT_STYLES}>
              <p className="font-body text-xs truncate text-body">
                {preview.name}
              </p>
            </div>
          </div>
        ) : null}
      </Sendable>
    </div>
  </Sortable>
)
